<template>
  <div id="app" v-if="contentOnly">
    <router-view></router-view>
  </div>
  <div id="app" v-else>
    <div v-if="canShowHeader" class="header-wrapper">
      <app-header />
      <div id="filter" class="container-fluid"> </div>
    </div>
    <div class="loading-app" v-if="isLoading">
      <Spinner />
    </div>
    <main v-else
      v-bind:class="{'container-fluid main-content': canShowHeader,
      'no-filter': canShowHeader && hideFilter, 'background-blur': showTermsDialog }">
      <router-view></router-view>
    </main>

    <!-- Terms and Conditions -->
    <Dialog
      title="Terms of Use"
      :visible.sync="showTermsDialog"
      width="720px"
      :show-close="false"
      :before-close="logout"
      :close-on-click-modal="false"
      :close-on-press-escape="false"
      class="default-dialog">
      <div class="dialog-content dialog-scroll" style="max-height:450px;">
        <iframe
          v-if="configurations && configurations.terms_and_conditions"
          :src="configurations.terms_and_conditions.s3_path"
          frameborder="0"
          style="border:0" width="100%" height="400px"
        />
      </div>
      <span slot="footer" class="dialog-footer">
        <Button type="secondary" size="mini" @click="logout();">Decline</Button>
        <Button type="primary" size="mini" @click="acceptTerms();">Accept</Button>
      </span>
    </Dialog>
    
    <!-- Portal target for file preview -->
    <portal-target name="file-preview"></portal-target>

    <EventPopup
      :show="showEventPopup"
      :isOnReportsPage="isOnReportsPage"
      :notificationData="currentNotificationData"
      @ok="handleOkClick"
      @go="handleGoClick"
      @close="closeEventPopup" />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import firebase from 'firebase';
import 'firebase/messaging';
import Header from '@/components/common/Header';
import Login from '@/components/modules/auth/Login';
import EventPopup from '@/components/common/EventPopup';
import FaviconNotification from '@/utils/faviconNotification';
// import AzureLogin from '@/components/modules/auth/AzureLogin';
import LoginValidation from '@/components/mixins/Login';
import { shouldPreserveRedirect } from '@/utils/redirect';
// import Firework from '@common/components/firework.vue';
// eslint-disable-next-line import/no-extraneous-dependencies
import '@common/asset/icons';
import setLocale from './utils/localisation';

export default {
  name: 'App',
  mixins: [LoginValidation],
  components: {
    'app-header': Header,
    Login,
    EventPopup,
  },
  data() {
    return {
      contentOnly: false,
      dialogVisible: true,
      showHeader: false,
      showEventPopup: false,
      isOnReportsPage: false,
      faviconNotification: null,
      isPageVisible: true,
      currentNotificationData: null,
    };
  },

  computed: {
    ...mapGetters({
      isLoading: 'app/isLoading',
      isLoggedIn: 'app/loggedIn',
      currentUser: 'app/currentUser',
      configurations: 'app/configurations',
      showTermsDialog: 'app/showTermsDialog',
      environmentVariables: 'app/environmentVariables',
    }),

    hideFilter() {
      return this.$route.meta.filter === false;
    },
    canShowHeader() {
      return this.showHeader || (this.isLoggedIn && this.currentUser.reset_password === false);
    },
  },
  async created() {
    if (['Map', 'TrackMap', 'ActiveTripMap', 'DocumentListing', 'PackingAudit', 'HereMap']
      .includes(this.$router.history.current.name)) {
      this.contentOnly = true;
    }
    try {
      if (!this.contentOnly && (this.$route.name === 'OpenLinkTrackDetails' || this.$route.name === 'ConsigneeTrackLink')) {
        this.showHeader = true;
      } else {
        if (this.$router.history.current.name === 'Logout') {
          const optimaHeaders = localStorage.getItem('Optima-selected-headers');
          const optimaInputPageHeaders = localStorage.getItem('optima-input-selected-headers');
          await this.$store.dispatch('app/logout', { data: { } });
          const tableParameters = localStorage.getItem('tableParameters');
          localStorage.clear();
          if (tableParameters) {
            localStorage.setItem('tableParameters', tableParameters);
          }
          if (optimaHeaders) {
            localStorage.setItem('Optima-selected-headers', optimaHeaders);
          }
          if (optimaInputPageHeaders) {
            localStorage.setItem('optima-input-selected-headers', optimaInputPageHeaders);
          }
          localStorage.setItem('tsm-user-token', '');
          const ssoCheck = await this.$store.dispatch('app/ssoCheck');
          let loginName = 'Login';
          if(!this.$route.query.pandoLogin){
            if (ssoCheck.status === 404) {
              loginName = 'Login';
            } else {
              loginName = 'SSOLogin';
            }
          }
          if (this.$route.query.serviceUrl) {
            this.$router.push({
              name: loginName,
              query: { serviceUrl: this.$route.query.serviceUrl },
            });
          } else {
            this.$router.push({ name: loginName });
          }
        } else if (this.$router.history.current.name !== 'Login') {
          localStorage.setItem('lastUrl', JSON.stringify(this.$router.history.current));
        }
        if (!['Dispatch', 'Map', 'TrackMap', 'HereMap'].includes(this.$router.history.current.name)) {
          const result = await this.$store.dispatch('app/bootstrap');
          if (result.status === 200) {
            this.$store.commit('app/SET_IS_PAYMENT_ERROR', false);
          }
          if (result.status === 402) {
            this.$store.commit('app/SET_IS_PAYMENT_ERROR', true);
            this.$router.push({ name: 'Login' });
            return;
          }
          if (result &&
            result.configurations &&
            result.configurations.inlineManual &&
            result.configurations.inlineManual.api_key &&
            result.configurations.inlineManual.show
          ) {
            this.initializeInlineManual(result.configurations.inlineManual.api_key);
            window.inlineManualTracking = {
              uid: this.currentUser.id,
              email: this.currentUser.email,
              name: this.currentUser.name,
              roles: [this.currentUser.role.type],
            };

            const id = setInterval(() => {
              const check = this.createmanual();
              if (check) {
                clearInterval(id);
              }
            }, 5000);
          }

          const language = (result && result.configurations &&
            result.configurations.locales &&
            result.configurations.locales.default_locale) ?
            result.configurations.locales.default_locale : 'en';
          await setLocale(language);
          this.setFreshWidget(result);
          const ssoCheck = await this.$store.dispatch('app/ssoCheck');
          if (this.$route.query.serviceUrl) {
            if (result.status === 401) {
              const intendedUrl = sessionStorage.getItem('intended_url');
              const query = { serviceUrl: this.$route.query.serviceUrl };

              if (intendedUrl && shouldPreserveRedirect(intendedUrl)) {
                query.redirect_url = intendedUrl;
                sessionStorage.removeItem('intended_url');
              }

              this.$router.push({
                name: 'Login',
                query,
              });
            } else {
              this.validateServiceUrl(this.$route.query.serviceUrl);
            }
          } else if (result.status === 401 && !this.$route.query.pandoLogin) {
            // Added redirect_url query param for RFQ approval URLs
            const intendedUrl = sessionStorage.getItem('intended_url');
            const loginRoute = { name: ssoCheck.status === 404 ? 'Login' : 'SSOLogin' };
            if (intendedUrl && shouldPreserveRedirect(intendedUrl)) {
              loginRoute.query = { redirect_url: intendedUrl };
              sessionStorage.removeItem('intended_url');
            }
            this.$router.push(loginRoute);
          } else if (result.current_user.reset_password) {
            this.$router.push({ name: 'PasswordReset' });
          } else if (this.$router.history.current.name === 'Login') {
            this.$store.commit('app/SET_IS_PAYMENT_ERROR', false);
            this.validateRedirect(result);
          } else if (!result.current_user.has_accepted_terms_conditions &&
              result.configurations.terms_and_conditions &&
              result.configurations.terms_and_conditions.is_enabled) {
            this.$store.commit('app/SHOW_TERMS_DIALOG', true);
          } else {
            this.$store.commit('app/SET_IS_PAYMENT_ERROR', false);
          }
        }
      }
      this.initaialzeFirebase();
      this.initializeFaviconNotification();
      this.setupVisibilityTracking();
    } catch (err) {
      console.log('err..', err);
      throw (err);
    }
  },
  methods: {
    initaialzeFirebase() {
      try {
        const firebaseCreds = {
          apiKey: this.environmentVariables.fb_api_key,
          authDomain: this.environmentVariables.fb_auth_domain,
          projectId: this.environmentVariables.fb_projectId,
          storageBucket: this.environmentVariables.fb_bucket,
          messagingSenderId: this.environmentVariables.fb_ms_id,
          appId: this.environmentVariables.fb_app_id,
          measurementId: this.environmentVariables.fb_me_id,
        };
        const isFirebaseEnabled = () => Object.keys(firebaseCreds).every(key =>
          (firebaseCreds[key].trim()),
        );
        if (isFirebaseEnabled()) {
          firebase.initializeApp({
            apiKey: firebaseCreds.apiKey,
            projectId: firebaseCreds.projectId,
            messagingSenderId: firebaseCreds.messagingSenderId,
            appId: firebaseCreds.appId,
          });
          // Registering the service worker for FCM
          navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' })
            .then((registration) => {
              if (registration.active) {
                registration.active.postMessage({
                  type: 'FIREBASE_CONFIG',
                  config: firebaseCreds,
                });
              }
              navigator.serviceWorker.ready.then((reg) => {
                reg.active.postMessage({
                  type: 'FIREBASE_CONFIG',
                  config: firebaseCreds,
                });
              });
            });
          navigator.serviceWorker.onmessage = (event) => {
            // Handle different message types from service worker
            if (event.data && event.data.type === 'FCM_MESSAGE') {
              const payload = event.data.payload;
              if (payload.notification || payload.data) {
                const message = payload.notification || {
                  title: payload.data.title,
                  body: payload.data.body
                };
                // Attach data payload so link (e.g. /riq/{id}) is available for routing
                if (payload.data) {
                  message.data = payload.data;
                }
                this.$store.dispatch('order/newFCMUpdateReceived', message);
                this.handleReportNotification(message);
              }
            }
            // Handle notification click from service worker
            else if (event.data && event.data.type === 'NOTIFICATION_CLICKED') {
              const { targetLink, notificationData, isOnReportsPage } = event.data;
              this.currentNotificationData = notificationData;
              this.showEventPopup = true;
              this.isOnReportsPage = isOnReportsPage;
              this.faviconNotification.hideNotification();
              
              // Store the notification data with the targetLink for routing
              const fullNotificationData = {
                title: notificationData.title,
                body: notificationData.body,
                data: notificationData,
                targetLink: targetLink
              };
              localStorage.setItem('pando_notification_data', JSON.stringify(fullNotificationData));
            }
            else if (event.data && event.data.notification && event.data.notification.body) {
              const message = event.data.notification;
              // Attach data payload so link (e.g. /riq/{id}) is available for routing
              if (event.data.data) {
                message.data = event.data.data;
              }
              this.$store.dispatch('order/newFCMUpdateReceived', message);
              this.handleReportNotification(message);
            }
          };
          firebase
            .messaging()
            .requestPermission()
            .then(() => {
              const vapidKey = this.environmentVariables.fb_vapid_key;
              const getTokenOptions = vapidKey ? { vapidKey } : {};
              return firebase.messaging().getToken(getTokenOptions)
                .then((token) => {
                  this.$store.dispatch('app/fcmToken', token);
                })
                .catch((err) => {
                  console.log('error while generating token', err);
                });
            })
            .catch((err) => {
              console.log('Notification permission not granted', err);
            });
          firebase.messaging().onMessage((payload) => {
            // Only dispatch to store, service worker handles notification
            const message = payload.notification || {
              title: payload.data && payload.data.title,
              body: payload.data && payload.data.body,
            };
            // Attach data payload so link (e.g. /riq/{id}) is available for routing
            if (payload.data) {
              message.data = payload.data;
            }
            this.$store.dispatch('order/newFCMUpdateReceived', message);
            this.handleReportNotification(message);
          });
        }
      } catch (error) {
        console.log(error.stack);
      }
    },
    async logout() {
      this.$store.commit('app/SHOW_TERMS_DIALOG', false);
      const optimaHeaders = localStorage.getItem('Optima-selected-headers');
      const optimaInputPageHeaders = localStorage.getItem('optima-input-selected-headers');
      await this.$store.dispatch('app/logout', { data: { } });
      const tableParameters = localStorage.getItem('tableParameters');
      localStorage.clear();
      if (tableParameters) {
        localStorage.setItem('tableParameters', tableParameters);
      }
      if (optimaHeaders) {
        localStorage.setItem('Optima-selected-headers', optimaHeaders);
      }
      if (optimaInputPageHeaders) {
        localStorage.setItem('optima-input-selected-headers', optimaInputPageHeaders);
      }
      localStorage.setItem('tsm-user-token', '');
      const result = await this.$store.dispatch('app/ssoCheck');
      if(!this.$route.query.pandoLogin){
      if (result.status === 404) {
        this.$router.push({ name: 'Login' });
      } else {
        this.$router.push({ name: 'SSOLogin' });
      }
    }
    },
    async acceptTerms() {
      await this.$store.dispatch('app/acceptTerms');
      this.$store.commit('app/SHOW_TERMS_DIALOG', false);
    },
    validateServiceUrl(serviceUrl) {
      if (serviceUrl.includes('-ct')) {
        this.$router.push({ name: 'ControlTower',
          query: { serviceUrl: this.$route.query.serviceUrl } });
      } else if (serviceUrl.includes('-pack')) {
        this.$router.push({ name: 'Pack',
          query: { serviceUrl: this.$route.query.serviceUrl } });
      } else if (serviceUrl.includes('-fap')) {
        this.$router.push({ name: 'Fap',
          query: { serviceUrl: this.$route.query.serviceUrl } });
      } else if (serviceUrl.includes('-ohub')) {
        this.$router.push({ name: 'OrderHub',
          query: { serviceUrl: this.$route.query.serviceUrl } });
      } else if (serviceUrl.includes('localhost')) {
        this.$router.push({ name: 'OrderHub',
          query: { serviceUrl: this.$route.query.serviceUrl } });
      }
    },
    initializeInlineManual(apiKey) {
      try {
        const inlineWriterScriptElement = document.createElement('script');
        const t = document.getElementsByTagName('script')[0];
        inlineWriterScriptElement.setAttribute('async', 1);
        inlineWriterScriptElement.setAttribute('charset', 'UTF-8');
        t.parentNode.insertBefore(inlineWriterScriptElement, t);
        const baseUrl = this.environmentVariables.il_ml_base_url.replace('API_KEY', apiKey);
        inlineWriterScriptElement.setAttribute('src', baseUrl);
        document.head.appendChild(inlineWriterScriptElement);
      } catch (error) {
        console.log('Inline Manual Initialization Failed', error);
      }
    },
    createmanual() {
      try {
        // eslint-disable-next-line no-undef
        createInlineManualPlayer(window.inlineManualPlayerData);
        return true;
      } catch (error) {
        return false;
      }
    },
    showInAppNotification(message, data) {
      // Show in-app notification
      console.log('In-app notification:', message);
    },
    showEventNotification(message, data) {
      // Show popup with event data
      this.currentEventData = {
        title: message.title,
        body: message.body,
        data: data,
      };
      this.showEventPopup = true;
    },
    handleReportNotification(message) {
      const isOnReportsPage = this.$route.path.includes('/reportsv2');
      const isOnRiqPage = this.$route.path.includes('/riq');
      this.isOnReportsPage = isOnReportsPage;

      // Update reactive notification data for EventPopup prop
      this.currentNotificationData = message;

      // Store notification data for routing use in handleGoClick/handleOkClick
      localStorage.setItem('pando_notification_data', JSON.stringify(message));

      // If user is active on current tab, show only popup
      if (this.isPageVisible) {
        this.showEventPopup = true;
      } else {
        this.faviconNotification.showNotification();
      }
    },
    closeEventPopup() {
      this.showEventPopup = false;
      this.faviconNotification.hideNotification();
    },
    handleOkClick() {
      this.faviconNotification.hideNotification();
      this.showEventPopup = false;
      
      // Get notification data to determine what to refresh
      const storedData = localStorage.getItem('pando_notification_data');
      const notificationData = storedData ? JSON.parse(storedData) : null;
      
      if (this.isOnReportsPage) {
        this.$root.$emit('refreshReportList');
      } else if (notificationData) {
        // Check for target route in multiple possible locations
        let targetRoute = null;
        if (notificationData.targetLink) {
          targetRoute = notificationData.targetLink;
        } else if (notificationData.data && notificationData.data.link) {
          targetRoute = notificationData.data.link;
        } else if (notificationData.link) {
          targetRoute = notificationData.link;
        }
        
        if (targetRoute && (this.$route.path === targetRoute || this.$route.path.startsWith(targetRoute))) {
          // We're on the target page, refresh it
          if (targetRoute.startsWith('/riq/')) {
            this.$root.$emit('refreshRiqDetails');
          }
        }
      }
    },
    handleGoClick() {
      this.faviconNotification.hideNotification();
      
      // Get notification data to determine target route
      const storedData = localStorage.getItem('pando_notification_data');
      const notificationData = storedData ? JSON.parse(storedData) : null;
      
      // Check if notification has a specific link (like RIQ details)
      let targetRoute = '/reportsv2'; // default
      if (notificationData) {
        // First check if targetLink is directly available (from service worker)
        if (notificationData.targetLink) {
          targetRoute = notificationData.targetLink;
        }
        // Then check if it's in the data.link field (from FCM payload)
        else if (notificationData.data && notificationData.data.link) {
          targetRoute = notificationData.data.link;
        }
        // Finally check if it's in the link field (direct FCM)
        else if (notificationData.link) {
          targetRoute = notificationData.link;
        }
      }
      
      // Open target page in new tab when Go clicked on other pages
      window.open(targetRoute, '_blank');
      this.showEventPopup = false;
    },
    initializeFaviconNotification() {
      this.faviconNotification = new FaviconNotification();
      this.faviconNotification.init();
    },
    setupVisibilityTracking() {
      document.addEventListener('visibilitychange', () => {
        this.isPageVisible = !document.hidden;

        if (this.isPageVisible) {
          this.faviconNotification.markTabActive();
          if (localStorage.getItem('pando_notification_active') === 'true') {
            const isOnReportsPage = this.$route.path.includes('/reportsv2');
            this.isOnReportsPage = isOnReportsPage;
            this.showEventPopup = true;
            this.faviconNotification.hideNotification();
          }
        } else {
          this.faviconNotification.markTabInactive();
        }
      });

      window.addEventListener('focus', () => {
        this.isPageVisible = true;
        this.faviconNotification.markTabActive();

        if (localStorage.getItem('pando_notification_active') === 'true') {
          const isOnReportsPage = this.$route.path.includes('/reportsv2');
          this.isOnReportsPage = isOnReportsPage;
          this.showEventPopup = true;
          this.faviconNotification.hideNotification();
        }
      });

      window.addEventListener('blur', () => {
        this.isPageVisible = false;
        this.faviconNotification.markTabInactive();
      });

      // Clean up on page unload
      window.addEventListener('beforeunload', () => {
        this.faviconNotification.markTabInactive();
      });
    },
  },
};
</script>

<style lang="scss">
@import '~@common/styles-shipper/app';
@import 'vue-directive-tooltip/css/index.css';
@import 'vue-airbnb-style-datepicker/dist/styles.css';
@import '~@common/style/apps';
@import '~@common/asset/style_v1/apps';
@import 'vue-multiselect/dist/vue-multiselect.min.css';

.main-content {
  background: #f8f8f8;
  margin-top: 100px;
  min-height: 100%;
  padding-top: 20px;
}

.no-filter {
  margin-top: 50px;
}

.loading-app {
  text-align: center;
  margin-top: 120px;
  font-size: 2rem;
}
</style>
